import { useEffect, useRef, useState } from 'react';

interface StaggerTextProps {
  text: string;
  className?: string;
  delay?: number;
  staggerDelay?: number;
}

export default function StaggerText({
  text,
  className = '',
  delay = 0,
  staggerDelay = 0.04,
}: StaggerTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -30px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const words = text.split(' ');
  let charIndex = 0;

  return (
    <span ref={ref} className={`inline-block ${className}`}>
      {words.map((word, wi) => {
        const wordStart = charIndex;
        charIndex += word.length;
        if (wi < words.length - 1) charIndex += 1;

        return (
          <span key={wi} className="inline-block whitespace-nowrap">
            {word.split('').map((char, ci) => (
              <span
                key={ci}
                className="inline-block"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                  transitionProperty: 'opacity, transform',
                  transitionDuration: '0.5s',
                  transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                  transitionDelay: `${delay + (wordStart + ci) * staggerDelay}s`,
                }}
              >
                {char}
              </span>
            ))}
            {wi < words.length - 1 && (
              <span
                className="inline-block"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transition: `opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${delay + (wordStart + word.length) * staggerDelay}s`,
                }}
              >
                &nbsp;
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}